/**
 * Azure Connection — admin (`scope=platform`) + tenant (`scope=tenant`).
 *
 * Sits alongside the AWS Connection so operators on Microsoft Azure can
 * link their own database. Same UI shell for both scopes:
 *   /dashboard/admin/azure       → scope=platform (system_admin only)
 *   /dashboard/settings/azure    → scope=tenant   (any tenant admin)
 *
 * Pattern: the operator pastes their Azure Cosmos DB for MongoDB
 * connection string (RU or vCore — both speak the MongoDB wire
 * protocol). The backend encrypts it at rest (AES-256-GCM) and NEVER
 * returns it — only a masked scheme://•••@host form. "Verify" does a
 * REAL live ping + buildInfo against their cluster.
 */
import React, { useEffect, useState } from 'react';
import { Database, CheckCircle2, Loader2, X, Activity, Shield, Heart, AlertCircle, KeyRound, Lock } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AzureConnection({ scope = 'tenant' }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [healthCheck, setHealthCheck] = useState(null);
  const [runningHealth, setRunningHealth] = useState(false);
  const [form, setForm] = useState({
    connection_string: '', db_kind: 'cosmos_mongo_vcore', database_name: '', notes: '',
  });

  const load = async () => {
    try {
      const { data: d } = await api.get(`/azure/connection/${scope}`);
      setData(d);
      setForm({
        connection_string: '',
        db_kind: d.db_kind || d.default_db_kind || 'cosmos_mongo_vcore',
        database_name: d.database_name || '',
        notes: d.notes || '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Azure connection');
    }
  };

  // Initial load + 20s heartbeat — matches the AWS page behaviour.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => { if (!cancelled) await load(); };
    tick();
    const t = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [scope]);

  const save = async () => {
    if (!form.connection_string.trim() && !data?.connection_string_masked) {
      toast.error('Connection string is required.');
      return;
    }
    setBusy(true);
    try {
      await api.put(`/azure/connection/${scope}`, form);
      toast.success('Azure connection saved. Click "Verify" to test it live.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data: r } = await api.post(`/azure/connection/${scope}/verify`);
      if (r.ok) {
        toast.success(`Connected · ${r.server?.latency_ms}ms · ${r.server?.is_cosmos ? 'Azure Cosmos DB' : 'MongoDB on Azure'}${r.server?.version ? ` · wire v${r.server.version}` : ''}`);
      } else {
        toast.error(`Verify failed: ${r.error || 'connection rejected'}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verify failed');
    } finally { setVerifying(false); }
  };

  const runHealth = async () => {
    setRunningHealth(true);
    setHealthCheck(null);
    try {
      const { data: r } = await api.post(`/azure/connection/${scope}/health`);
      setHealthCheck(r);
      if (r.summary?.failing > 0) toast.error(`${r.summary.failing}/${r.summary.total} probes failing`);
      else toast.success(`All ${r.summary.passing} probes healthy.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Health check failed');
    } finally { setRunningHealth(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Azure? The stored connection string will be deleted.')) return;
    try { await api.delete(`/azure/connection/${scope}`); toast.success('Disconnected.'); setHealthCheck(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="pal-card pal-grid-bg p-5"><span className="pal-bracket">[AZURE · LOADING…]</span></div>;

  const kinds = data.db_kinds_catalog || [];
  const status = data.status || 'not_configured';

  return (
    <div className="space-y-5" data-testid="azure-connection-page">
      {/* Hero */}
      <div className="pal-card pal-card-live pal-grid-bg p-5 flex items-start gap-4">
        <div className="h-12 w-12 rounded-md flex items-center justify-center shrink-0 border"
          style={{ backgroundColor: 'rgba(0,120,212,0.10)', color: '#4DA3E8', borderColor: 'rgba(0,120,212,0.40)' }}>
          <Database size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="pal-bracket">[AZURE · {scope === 'platform' ? 'PLATFORM' : 'TENANT'} DATABASE CONNECTION]</div>
          <h1 className="display text-2xl mt-0.5">Link your Microsoft Azure database.</h1>
          <p className="text-sm mt-1 max-w-2xl opacity-70">
            Connect the Azure Cosmos DB for MongoDB account (RU or vCore) that {scope === 'tenant' ? 'this tenant' : 'the platform'} should use. The connection string is encrypted at rest (AES-256-GCM) and never returned by the API — verification is a real live ping against <em>your</em> cluster.
          </p>
        </div>
        <span className={`pal-status ${status === 'connected' ? 'pal-status-ok' : status === 'pending_verification' ? 'pal-status-warn' : 'pal-status-violet'} inline-flex items-center gap-1.5`}>
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${status === 'connected' ? 'bg-emerald-400' : status === 'pending_verification' ? 'bg-amber-400' : 'bg-[var(--ve-violet-bright)]'}`}>
            <span className={`absolute inset-0 rounded-full opacity-70 ${status === 'connected' ? 'bg-emerald-400' : status === 'pending_verification' ? 'bg-amber-400' : 'bg-[var(--ve-violet-bright)]'} animate-ping`}/>
          </span>
          {String(status).replace('_', ' ').toUpperCase()}
        </span>
      </div>

      {/* Live data-flow strip */}
      <div className="pal-card pal-grid-bg p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="pal-bracket">[WHERE YOUR DATA LIVES · LIVE VIEW]</div>
          <div className="flex items-center gap-1.5 pal-mono text-[10px] opacity-70">
            <Activity size={11} className="text-[var(--ve-violet-bright)] animate-pulse"/>
            <span>HEARTBEAT · 20s</span>
            {data.verified_at && <span className="opacity-50">· verified {new Date(data.verified_at).toLocaleString()}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px] pal-mono">
          <span className="px-3 py-2 rounded border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)] flex items-center gap-1.5"><Shield size={11} className="text-[var(--ve-violet-bright)]"/>USER · {scope === 'platform' ? 'system_admin' : 'tenant_admin'}</span>
          <span className="opacity-50">→</span>
          <span className="px-3 py-2 rounded border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)]">TLS 1.3</span>
          <span className="opacity-50">→</span>
          <span className="px-3 py-2 rounded border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)]">FastAPI · K8s</span>
          <span className="opacity-50">→</span>
          <span className="px-3 py-2 rounded border border-sky-400/40 bg-sky-500/10 text-sky-300 flex items-center gap-1.5"><Database size={11}/>YOUR AZURE · {data.connection_string_masked || 'not linked yet'}</span>
        </div>
        <div className="mt-3 text-[11px] opacity-65 leading-relaxed">
          Azure Cosmos DB for MongoDB speaks the native MongoDB wire protocol — when connected, reads and writes for {scope === 'tenant' ? 'this tenant' : 'the platform'} can run <strong className="text-sky-300">inside your Azure subscription</strong>, under your own firewall and private-endpoint rules.
        </div>
      </div>

      {/* Step 1 — where to find the connection string */}
      <div className="pal-card pal-grid-bg p-5">
        <div className="pal-bracket mb-3">[STEP 1 · COPY YOUR CONNECTION STRING FROM THE AZURE PORTAL]</div>
        <ol className="text-sm opacity-75 space-y-1.5 list-decimal list-inside">
          <li>Azure Portal → your <strong>Cosmos DB for MongoDB</strong> account (or your MongoDB VM/AKS endpoint).</li>
          <li>Left menu → <strong>Settings → Connection strings</strong> → copy the <em>PRIMARY CONNECTION STRING</em>.</li>
          <li>Under <strong>Networking</strong>, allow access from our platform egress IPs, or enable "Accept connections from within public Azure datacenters" while testing.</li>
        </ol>
        <div className="mt-3 pal-mono text-[10px] opacity-55 flex items-center gap-1.5">
          <Lock size={10} className="text-[var(--ve-violet-bright)]" /> Stored encrypted (AES-256-GCM) · never displayed again after saving · masked as {`scheme://•••@host`}
        </div>
      </div>

      {/* Step 2 — form */}
      <div className="pal-card pal-grid-bg p-5">
        <div className="pal-bracket mb-3">[STEP 2 · PASTE YOUR DATABASE DETAILS]</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 block mb-1">CONNECTION STRING</label>
            <input value={form.connection_string} onChange={(e) => setForm({ ...form, connection_string: e.target.value })}
              type="password" autoComplete="off"
              placeholder={data.connection_string_masked ? `${data.connection_string_masked} (paste blank to keep)` : 'mongodb://<account>:<key>@<account>.mongo.cosmos.azure.com:10255/?ssl=true...'}
              data-testid="azure-conn-string"
              className="w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]" />
            {data.connection_string_masked && <div className="text-[9px] mt-1 opacity-50 pal-mono">Current: {data.connection_string_masked} (leave blank to keep)</div>}
          </div>
          <div>
            <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 block mb-1">DATABASE NAME (OPTIONAL)</label>
            <input value={form.database_name} onChange={(e) => setForm({ ...form, database_name: e.target.value })}
              placeholder="e.g. vigilant_tenant_db" data-testid="azure-db-name"
              className="w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]" />
          </div>
          <div>
            <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 block mb-1">NOTES</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Owner / subscription / rotation policy" data-testid="azure-notes"
              className="w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]" />
          </div>
        </div>

        <div className="pal-bracket mt-5 mb-2">[STEP 3 · WHICH AZURE DATABASE FLAVOUR IS THIS?]</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
          {kinds.map((k) => {
            const on = form.db_kind === k.id;
            return (
              <button key={k.id} type="button" onClick={() => setForm({ ...form, db_kind: k.id })}
                data-testid={`azure-db-kind-${k.id}`}
                className={`text-left px-3 py-3 rounded border transition-colors ${on ? 'border-sky-400/60 bg-sky-500/10' : 'border-[rgba(174,82,234,0.18)] hover:border-[var(--ve-violet)]/55 hover:bg-[rgba(174,82,234,0.04)]'}`}>
                <div className="flex items-center gap-2">
                  {on ? <CheckCircle2 size={13} className="text-sky-300" /> : <KeyRound size={13} className="opacity-50" />}
                  <span className="pal-mono text-[12px] font-bold">{k.label}</span>
                  {k.is_default && <span className="pal-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300">recommended</span>}
                </div>
                <div className="pal-mono text-[10px] opacity-65 mt-1 leading-relaxed">{k.purpose}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={save} disabled={busy} data-testid="azure-save"
            className="px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)] disabled:opacity-50 flex items-center gap-1.5">
            {busy ? <Loader2 size={11} className="animate-spin" /> : null} SAVE CONNECTION
          </button>
          <button onClick={verify} disabled={verifying || status === 'not_configured'} data-testid="azure-verify"
            className="px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono border border-[rgba(174,82,234,0.40)] text-[var(--ve-violet-bright)] hover:bg-[rgba(174,82,234,0.10)] disabled:opacity-40 flex items-center gap-1.5">
            {verifying ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} VERIFY NOW
          </button>
          <button onClick={runHealth} disabled={runningHealth || status !== 'connected'} data-testid="azure-health-check"
            className="px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 flex items-center gap-1.5">
            {runningHealth ? <Loader2 size={11} className="animate-spin" /> : <Heart size={11} />} RUN HEALTH CHECK
          </button>
          {status !== 'not_configured' && (
            <button onClick={disconnect} data-testid="azure-disconnect"
              className="ml-auto px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono border border-rose-400/30 text-rose-400 hover:bg-rose-500/10 flex items-center gap-1.5">
              <X size={11} /> DISCONNECT
            </button>
          )}
        </div>
        {data.last_verify_error && status === 'error' && (
          <div className="mt-3 p-3 rounded border border-rose-400/40 bg-rose-500/5 pal-mono text-[10px] text-rose-300/90 break-words" data-testid="azure-last-error">
            LAST VERIFY ERROR · {data.last_verify_error}
          </div>
        )}
      </div>

      {/* Bloomberg-style health grid — identical shape to the AWS one */}
      {healthCheck && (
        <div className="pal-card pal-grid-bg p-5" data-testid="azure-health-grid">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="pal-bracket">[LIVE HEALTH CHECK · {healthCheck.summary.passing}/{healthCheck.summary.total} PASSING]</div>
            <div className="pal-mono text-[10px] opacity-60">checked {new Date(healthCheck.summary.checked_at).toLocaleString()}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {healthCheck.grid.map((g) => (
              <div key={g.service} data-testid={`azure-health-${g.service}`}
                className={`p-3 rounded border ${g.ok ? 'border-emerald-400/40 bg-emerald-500/5' : 'border-rose-400/40 bg-rose-500/5'}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`pal-mono text-[12px] font-bold uppercase ${g.ok ? 'text-emerald-300' : 'text-rose-300'}`}>{g.service}</span>
                  <span className={`inline-flex items-center gap-1 pal-mono text-[10px] uppercase tracking-[0.15em] ${g.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {g.ok ? <><CheckCircle2 size={10}/> OK</> : <><AlertCircle size={10}/> FAIL</>}
                    {typeof g.latency_ms === 'number' && <span className="opacity-60">· {g.latency_ms}ms</span>}
                  </span>
                </div>
                {g.ok ? (
                  <div className="pal-mono text-[10px] opacity-75 leading-relaxed">{g.detail}</div>
                ) : (
                  <div className="pal-mono text-[10px] text-rose-300/80 leading-relaxed break-words">{g.error}</div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 pal-mono text-[10px] opacity-55 leading-relaxed">
            Each probe is a read-only wire-protocol command against your Azure endpoint. Nothing about your data leaves your subscription during this check.
          </div>
        </div>
      )}
    </div>
  );
}
